/**
 * Consent Document Upload Page
 * Allows users to upload or capture consent documents.
 */

import React, { useState, useEffect } from 'react';
import ConsentHeader from './consent-header';
import { Form, Row, Col, Button, Space, message, Upload, Modal } from 'antd';
import { Camera, useTranslation } from 'soxo-bootstrap-core';
import { DocDetails } from '../../models';
import ConsentFilesList from './consent-file-list';
import { UploadOutlined, ArrowRightOutlined } from '@ant-design/icons';
import DocumentPreview from '../document-preview/document-preview';
import './consent-uploader.scss';
import ConsentFileSelector from './consent-file-select';

/**
 * Document type code mapping.
 * Extend this object if you want to add more document types later.
 */
export const documentCodes = {
    consent: 'CST',
    emid: 'EMID',
};

const ConsentUploader = ({ id, documentMode, callback, onPremiseUrl, props, fileUploadExtensionSupport, maxFileUploadSize, billId ,documentCode}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [uploadedFiles, setUploadedFiles] = useState([]); // All files (with status)

    const [loading, setLoading] = useState(false); // Loading state

    const [file, setFile] = useState(null); // Selected file

    const [openPreview, setOpenPreview] = useState(false); // Preview modal

    const [selectedDocCode, setSelectedDocCode] = useState(documentCode); // Selected document code

    const [selectedFileTypeDetails, setSelectedFileTypeDetails] = useState(null); // Selected file type details


    // Load uploaded files
    useEffect(() => {
        loadUploadedFiles();
    }, []);

    // Load uploaded files from backend
    const loadUploadedFiles = async (docCode = selectedDocCode, otherDetailsRecord = selectedFileTypeDetails) => {
        try {
            const refId = documentMode === 'OPBILL' ? billId : id;
            const result = await DocDetails.loadFilesById(refId, onPremiseUrl);

            // Sort descending by docdet_id (newest first)
            const sortedResult = result.sort((a, b) => Number(b.docdet_id) - Number(a.docdet_id));

            // Filter only active consent files
            const consentFiles = sortedResult
                .filter((file) => file.docdet_active === 'Y' && file.docFileTypes.file_type_code === docCode)
                .map((file) => {
                    let parsedOtherDetails = {};

                    try {
                        if (file.docdet_otherdetails) {
                            parsedOtherDetails = JSON.parse(file.docdet_otherdetails);
                        } else {
                            parsedOtherDetails = otherDetailsRecord || {};
                        }
                    } catch (err) {
                        console.warn('Invalid JSON in docdet_otherdetails for file:', file.docdet_filename, err);
                        parsedOtherDetails = otherDetailsRecord || {};
                    }

                    return {
                        docdet_id: file.docdet_id,
                        name: file.docdet_filename,
                        type: file.docdet_nativefiletype,
                        status: 'Uploaded',
                        otherDetails: parsedOtherDetails, // always object
                    };
                });

            setUploadedFiles(consentFiles);
        } catch (error) {
            console.error('Error loading consent files:', error);
        }
    };

    // Add new file(s) as "Pending"
    const onUpload = (attachments) => {
        const newFiles = attachments.map((file) => ({
            id: `${Date.now()}-${Math.random()}`, // Generate unique ID
            name: file.name,
            status: 'Pending',
            type: file.type?.includes('image') ? 'image' : 'file',
            rawFile: file,
            otherDetails: selectedFileTypeDetails || '{}'
        }));

        setUploadedFiles((prev) => [...newFiles, ...prev]);
        form.setFieldsValue({ files: attachments });
    };

    // Handle file change
    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    // Update specific file status
    const updateFileStatus = (fileName, status) => {
        setUploadedFiles((prev) => prev.map((file) => (file.name === fileName ? { ...file, status } : file)));
    };

    /**
     * Uploads a selected file to the backend API.
     *
     * Converts the file to Base64, prepares the request payload,
     * and sends it to the server using `DocDetails.documentUpload`.
     * Updates the file status in the UI based on the upload result.
     *
     * @param {Object} values - Form values containing file details (e.g., filename, fileType).
     * @returns {Promise<void>}
     */

    const uploadFileToAPI = async (values) => {
        const fileName = file.name;

        const toBase64 = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = (error) => reject(error);
            });

        try {
            const base64File = await toBase64(file);

            let headers = {
                db_ptr: localStorage.db_ptr || process.env.REACT_APP_DB_PTR,
                Authorization: 'Bearer ' + localStorage.access_token,
                'Content-Type': 'application/json',
            };

            // Initialize payload before using
            let payload = {};

            if (documentMode === 'OPBILL') {
                payload = {
                    ...payload,
                    bill_id: billId,
                    docdet_refptr: billId,
                    docdet_refid: billId,
                    docdet_refdetails: 'OPBILL details',
                    docdet_refmode: documentMode,
                    document_type: 'pdf',
                    document_code: selectedDocCode, 
                    files: base64File,
                };
            } else {
                payload = {
                    ...payload,
                    appointment_id: id,
                    docdet_refptr: id,
                    docdet_refid: id,
                    docdet_filename: values.filename ? values.filename : values.fileType,
                    docdet_refmode: documentMode,
                    document_type: 'pdf',
                    document_code: selectedDocCode, 
                    files: base64File,
                };
            }

            const formData = JSON.stringify(payload); //sending as json

            const result = await DocDetails.documentUpload(formData, onPremiseUrl, headers);

            if (result.message === 'success') {
                message.success('File Uploaded Successfully');
                updateFileStatus(fileName, 'Uploaded'); 
                loadUploadedFiles(selectedDocCode, selectedFileTypeDetails); 
            } else {
                message.error('File upload failed');
                updateFileStatus(fileName, 'Failed'); 
            }
        } catch (err) {
            message.error('Upload failed. Please try again.');
            updateFileStatus(fileName, 'Failed');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Uploads all pending files one by one.
     * Shows success or error messages for each,
     * then triggers the callback after completion.
     */

    async function onFinish() {
        if (loading) return; // prevent multiple uploads

        setLoading(true);

        const pendingFiles = uploadedFiles.filter((f) => f.status !== 'Uploaded');

        if (pendingFiles.length === 1) {
            const file = pendingFiles[0];

            try {
                await uploadFileToAPI(file);
            } catch (err) {
                message.error(`${file.name} upload failed`);
            }
        } else {
            for (const file of pendingFiles) {
                try {
                    await uploadFileToAPI(file);
                } catch {
                    message.error(`${file.name} failed`);
                }
            }
        }
        // callback();
    }

    /**
     * Uploads selected consent documents and updates their status.
     *
     * @param {File[]} selectedImages - Files to upload.
     * @param {number} [baseIndex=0] - Base index for file numbering.
     *
     * Displays a loading message, uploads each file using
     * `DocDetails.documentUpload`, updates file statuses (Uploading, Uploaded, Failed),
     * reloads the uploaded file list, and triggers a callback after completion.
     */

    const onConsentUpload = (selectedImages, baseIndex = 0) => {
        const newFiles = selectedImages.map((ele) => ({
            name: ele.name,
            time: 'Just now',
            status: 'Uploading',
            type: 'image',
        }));
        setUploadedFiles((prev) => [...newFiles, ...prev]);

        Promise.all(
            selectedImages.map((ele, index) => {
                const formData = new FormData();
                formData.append('files', ele);
                formData.append('index', baseIndex + (index + 1));
                formData.append('document_code', selectedDocCode);
                formData.append('docdet_refmode', documentMode);
                if (documentMode === 'OPBILL' ) {
                formData.append('bill_id', billId);
               } else {
               formData.append('appointment_id', id);
              }

                const headers = {
                    db_ptr: localStorage.db_ptr || process.env.REACT_APP_DB_PTR,
                    Authorization: 'Bearer ' + localStorage.access_token,
                };
                return DocDetails.documentUpload(formData, onPremiseUrl, headers);
            })
        ).then((result) => {
            if (result[0]?.success) {
                message.success('File Uploaded Successfully');
                setUploadedFiles((prev) => prev.map((file) => ({ ...file, status: 'Uploaded' })));
                loadUploadedFiles(selectedDocCode, selectedFileTypeDetails); 
                callback && callback() ;
            } else {
                message.error('File Upload Failed');
                setUploadedFiles((prev) => prev.map((file) => ({ ...file, status: 'Failed' })));
                callback && callback() ;
            }
        });
    };

    /**
     * Deletes a file from the list and marks it inactive on the server.
     * Shows an error message if deletion fails.
     */
    const handleDeleteFile = async (file) => {
        setUploadedFiles((prev) =>
            prev.filter((f) => {
                if (file.docdet_id) {
                    // Delete uploaded file by backend ID
                    return f.docdet_id !== file.docdet_id;
                } else {
                    // Delete pending file by local ID
                    return f.id !== file.id;
                }
            })
        );

        try {
            DocDetails.documentUpdate(onPremiseUrl, file.docdet_id, { docdet_active: 'N' });
        } catch {
            message.error('Failed to delete file');
        }
    };

    // Disable upload button only if all files are uploaded (no pending)
    const allUploaded = uploadedFiles.length > 0 && uploadedFiles.every((file) => file.status === 'Uploaded');

    return (
        <>
            <Form form={form} onFinish={onFinish} layout="vertical" requiredMark={false} className='consent-upload-section'>
                <ConsentHeader title="Upload Document" description="You can upload a document from your device or take a photo using your camera." />

                <Row gutter={[16, 16]} align="middle"  className='consent-upload-controls'>
                    <Col xs={24} md={8}>
                        {/* Document Type Selector */}
                        <ConsentFileSelector
                            form={form}
                            onTypeChange={(code,record) => {
                                setSelectedDocCode(code);
                                setSelectedFileTypeDetails(record); // store isPublishable info
                                loadUploadedFiles(code, record); // reload for that document type 
                            }}
                            FlieTypeShow={documentCode}
                        />
                        {/* Hidden field to hold document code */}
                        <Form.Item name="documentCode" hidden>
                            <input type="hidden" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={16}>
                        <Space size="middle" className='consent-upload-actions'>
                            <Form.Item name="files" rules={[{ required: true, message: t('Please select file') }]} style={{ marginTop: '30px' }}>
                                <Upload
                                    accept={props.fileUploadExtensionSupport || fileUploadExtensionSupport } //only show files with extension ".pdf,.PDF"
                                    beforeUpload={(file) => {
                                        // Prefer top-level prop if provided; fallback to nested; default 30MB
                                        const maxSizeMB = maxFileUploadSize || props?.maxFileUploadSize;
                                        const isWithinLimit = file.size / 1024 / 1024 <= maxSizeMB;

                                        if (!isWithinLimit) {
                                            message.error(`File must be smaller than or equal to ${props.maxFileUploadSize || maxFileUploadSize} MB`);
                                            return Upload.LIST_IGNORE; // Prevent adding this file
                                        }
                                        handleFileChange({ target: { files: [file] } });
                                        onUpload([file]);
                                        return false;
                                    }}
                                    showUploadList={false}
                                >
                                    <Button icon={<UploadOutlined />}>Browse</Button>
                                </Upload>
                            </Form.Item>

                            <div className='consent-upload-camera'>
                                <Camera multiple onUpload={onConsentUpload} mode="rear" label="Camera" />
                              
                            </div>
                        </Space>
                    </Col>
                </Row>

                {/* Dynamic file list with status */}
                <ConsentFilesList
                    files={uploadedFiles}
                    onDelete={handleDeleteFile}
                    onView={() => {
                        setOpenPreview(true);
                    }}
                    selectedDocCode={selectedDocCode} // pass currently selected code
                    referenceId={billId}
                    documentMode={documentMode}
                    onPremiseUrl={onPremiseUrl}
                    loadUploadedFiles={loadUploadedFiles} // Pass function down
                />

                <Modal
                    width="100%"
                    destroyOnClose
                    title={t('Consent File')}
                    open={openPreview}
                    okText="Okay"
                    onOk={() => setOpenPreview(false)}
                    onCancel={() => setOpenPreview(false)}
                >
                    <DocumentPreview
                        id={id || billId}
                        props={{ reportConfig: props.reportConfig || props.reportConfigs, onPremiseUrl, type: selectedDocCode }}
                        isPdfCameraHide={true}
                    />
                </Modal>

                <div className="form-footer">
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        className={`consent-upload-btn ${allUploaded ? 'disabled-btn' : ''}`}
                        disabled={allUploaded} // disable only when all files are uploaded
                    >
                        Upload <ArrowRightOutlined />
                    </Button>
                </div>
            </Form>
        </>
    );
};

export default ConsentUploader;